import { Component, ErrorInfo, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faRefresh, faHome } from '@fortawesome/free-solid-svg-icons';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        this.setState({ errorInfo });
        // Log error to analytics or error reporting service
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleReload = (): void => {
        window.location.reload();
    };

    handleGoHome = (): void => {
        window.location.href = '/';
    };

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-app flex items-center justify-center bg-slate-100 dark:from-slate-900 dark:to-slate-800 px-4">
                    <div className="max-w-md w-full text-center space-y-6">
                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-8 space-y-6 animate-fade-in">
                            <div className="w-16 h-16 mx-auto bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center">
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    className="w-8 h-8 text-rose-500 dark:text-rose-400"
                                />
                            </div>

                            <div className="space-y-2">
                                <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
                                    Đã xảy ra lỗi
                                </h1>
                                <p className="text-slate-600 dark:text-slate-400 text-sm">
                                    Xin lỗi, có lỗi không mong muốn đã xảy ra. Vui lòng thử lại sau.
                                </p>
                            </div>

                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <details className="text-left bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                                    <summary className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                                        Chi tiết lỗi (Developer mode)
                                    </summary>
                                    <pre className="mt-2 text-xs text-rose-600 dark:text-rose-400 overflow-auto max-h-40">
                                        {this.state.error.toString()}
                                        {this.state.errorInfo?.componentStack}
                                    </pre>
                                </details>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={this.handleReload}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
                                >
                                    <FontAwesomeIcon icon={faRefresh} className="w-4 h-4" />
                                    Tải lại trang
                                </button>
                                <button
                                    onClick={this.handleGoHome}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-medium rounded-lg transition-colors duration-200"
                                >
                                    <FontAwesomeIcon icon={faHome} className="w-4 h-4" />
                                    Về trang chủ
                                </button>
                            </div>
                        </div>

                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Nếu lỗi vẫn tiếp tục, vui lòng liên hệ hỗ trợ kỹ thuật.
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Hook version for functional components
export const useErrorHandler = () => {
    const handleError = (error: Error) => {
        console.error('Error handled:', error);
        // Can add error reporting service here
    };

    return { handleError };
};
